
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  fetchProvisioningStatus,
  ProvisioningStage,
  startProvisioning,
} from '@/composables/onboarding-v2/useShopProvisioning';
import { UserState } from '@/composables/User';

const POLL_MS = 3_000;
const TIMEOUT_MS = 5 * 60 * 1_000;
const STAGNATION_MS = 100_000;
const AUTO_REDIRECT_S = 10;

const STAGES = [
  {
    id: 'env',
    labelKey: 'completeV2.steps.liftOff.stages.env',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.env',
    icon: 'far fa-server',
  },
  {
    id: 'db',
    labelKey: 'completeV2.steps.liftOff.stages.db',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.db',
    icon: 'far fa-database',
  },
  {
    id: 'sync',
    labelKey: 'completeV2.steps.liftOff.stages.sync',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.sync',
    icon: 'far fa-rotate',
  },
  {
    id: 'ready',
    labelKey: 'completeV2.steps.liftOff.stages.ready',
    descriptionKey: 'completeV2.steps.liftOff.stageDesc.ready',
    icon: 'far fa-rocket-launch',
  },
];

const STAGE_TO_INDEX: Record<ProvisioningStage, number> = {
  queued: 0,
  db_create: 0,
  db_connect: 1,
  migrate: 1,
  seed: 2,
  finalize: 2,
  done: 3,
};

export default defineComponent({
  name: 'LiftOffStep',

  components: {
    StepShell,
    SpmButton,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const activeStage = ref(0);
    const ready = ref(false);
    const timedOut = ref(false);
    const countdown = ref(AUTO_REDIRECT_S);
    const countdownPct = computed(
      () => Math.max(0, (countdown.value / AUTO_REDIRECT_S) * 100),
    );

    const stages = computed(() => STAGES);

    const stageStatus = (i: number) => {
      if (ready.value) return 'done';
      if (i < activeStage.value) return 'done';
      if (i === activeStage.value) return 'active';
      return 'pending';
    };

    const applySnapshot = (stage: ProvisioningStage) => {
      activeStage.value = STAGE_TO_INDEX[stage] ?? 0;
      if (stage === 'done') ready.value = true;
    };

    let pollHandle: number | null = null;
    let countdownHandle: number | null = null;
    let timeoutHandle: number | null = null;
    let lastStage: ProvisioningStage | null = null;
    let lastStageChange = Date.now();

    const stopPoll = () => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    };

    const stopTimeout = () => {
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    };

    const stopCountdown = () => {
      if (countdownHandle !== null) {
        window.clearInterval(countdownHandle);
        countdownHandle = null;
      }
    };

    watch(ready, (value) => {
      if (!value) return;
      countdown.value = AUTO_REDIRECT_S;
      countdownHandle = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          stopCountdown();
          emit('next');
        }
      }, 1000);
    });

    onMounted(async () => {
      const shopId = UserState.activeShop?.id ?? 0;
      console.log(`[provisioning][LiftOffStep] mounted (shopId=${shopId})`);
      if (!shopId) {
        console.warn('[provisioning][LiftOffStep] no active shop — aborting');
        return;
      }

      const first = await startProvisioning(shopId);
      console.log(
        `[provisioning][LiftOffStep] initial snapshot stage=${first.stage} progress=${first.progress}`,
      );
      lastStage = first.stage;
      lastStageChange = Date.now();
      applySnapshot(first.stage);
      if (ready.value) {
        console.log(
          '[provisioning][LiftOffStep] already ready on mount — no polling needed',
        );
        return;
      }

      timeoutHandle = window.setTimeout(() => {
        if (!ready.value) {
          console.warn(
            `[provisioning][LiftOffStep] global TIMEOUT (${TIMEOUT_MS}ms) reached — switching to timeout screen`,
          );
          timedOut.value = true;
          stopPoll();
        }
      }, TIMEOUT_MS);

      pollHandle = window.setInterval(async () => {
        if (ready.value || timedOut.value) return;
        const snap = await fetchProvisioningStatus(shopId);
        applySnapshot(snap.stage);
        if (ready.value) {
          console.log(
            `[provisioning][LiftOffStep] reached DONE (stage=${snap.stage}) — stopping poll`,
          );
          stopPoll();
          stopTimeout();
          return;
        }
        if (snap.stage !== lastStage) {
          console.log(
            `[provisioning][LiftOffStep] stage changed: ${lastStage} → ${snap.stage}`,
          );
          lastStage = snap.stage;
          lastStageChange = Date.now();
          return;
        }
        const stagnationMs = Date.now() - lastStageChange;
        if (stagnationMs > STAGNATION_MS) {
          console.warn(
            `[provisioning][LiftOffStep] stagnation detected (stage="${snap.stage}" unchanged for ${stagnationMs}ms) — re-kicking provisioning`,
          );
          lastStageChange = Date.now();
          const relaunch = await startProvisioning(shopId);
          console.log(
            `[provisioning][LiftOffStep] re-kick returned stage=${relaunch.stage} progress=${relaunch.progress}`,
          );
          lastStage = relaunch.stage;
          applySnapshot(relaunch.stage);
          if (ready.value) {
            stopPoll();
            stopTimeout();
          }
        }
      }, POLL_MS);
    });

    onBeforeUnmount(() => {
      stopPoll();
      stopTimeout();
      stopCountdown();
    });

    return {
      t,
      stages,
      stageStatus,
      ready,
      timedOut,
      countdown,
      countdownPct,
    };
  },
});
